import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Star, TrendingUp, Shield, Clock } from 'lucide-react';
import DOMPurify from 'dompurify';

const BrandPage = () => {
  const { brand } = useParams();
  
  // Brand-specific data for SEO
  const brandData = {
    'amazon': {
      name: 'Amazon',
      logo: '🛒',
      rate: '88-93%',
      popularity: 5,
      description: 'Sell Amazon gift cards for the highest rates in the market',
      benefits: [
        'Highest demand and liquidity',
        'Consistent 88-93% rates year-round',
        'Fastest processing times',
        'No seasonal rate fluctuations'
      ],
      tips: [
        'Amazon cards always maintain premium rates',
        'Physical and digital cards accepted',
        'No expiration date concerns',
        'Best choice for immediate cash needs'
      ],
      marketShare: '35%',
      processingTime: '2-4 hours'
    },
    'apple': {
      name: 'Apple',
      logo: '🍎', 
      rate: '82-89%',
      popularity: 5,
      description: 'Convert Apple and iTunes gift cards to cash with premium rates',
      benefits: [
        'Premium rates for tech brand',
        'High demand from Apple users',
        'Covers App Store, iTunes, Apple Store',
        'Rates increase during product launches'
      ],
      tips: [
        'Best rates during iPhone/iPad launch seasons',
        'iTunes cards have same value as Apple Store cards',
        'Digital codes processed faster than physical cards',
        'Higher denominations get better rates'
      ],
      marketShare: '18%',
      processingTime: '2-6 hours'
    },
    'target': {
      name: 'Target',
      logo: '🎯',
      rate: '82-87%',
      popularity: 4,
      description: 'Sell Target gift cards for competitive cash rates',
      benefits: [
        'Strong retail brand demand',
        'Consistent rates throughout year',
        'Popular for household shopping',
        'Good liquidity in market'
      ],
      tips: [
        'Rates peak during back-to-school season',
        'Holiday seasons show rate increases',
        'Physical cards preferred by buyers',
        'Receipt not required but helps value'
      ],
      marketShare: '12%',
      processingTime: '3-8 hours'
    }
  };

  const currentBrand = brandData[brand] || {
    name: brand?.charAt(0).toUpperCase() + brand?.slice(1) || 'Gift Card',
    logo: '🎁',
    rate: '70-85%',
    popularity: 3,
    description: `Sell ${brand} gift cards for competitive cash rates`,
    benefits: [],
    tips: [],
    marketShare: 'N/A',
    processingTime: '4-8 hours'
  };

  useEffect(() => {
    // Analytics only (page title/description are handled by Helmet below)
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        'page_title': `Sell ${currentBrand.name} Gift Cards`,
        'page_location': window.location.href,
        'event_category': 'brand_seo'
      });
    }
  }, [brand, currentBrand]);

  const pageTitle = `Sell ${currentBrand.name} Gift Cards for Cash | Cashifygcmart`;
  const pageDescription = `${currentBrand.description}. Get ${currentBrand.rate} of face value with same-day payment. Trusted ${currentBrand.name} gift card exchange service.`;
  const canonicalUrl = `https://cashifygcmart.com/brand/${brand}`;

  // Brand-specific schema — Service (not Product), because CashifyGCMart is a
  // service that buys gift cards from users, not an e-commerce store selling them.
  // Using Product/Offer here triggers Google's Merchant listings validator to
  // demand fields we don't have (image, availability, shippingDetails, returns).
  const brandSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${currentBrand.name} Gift Card Exchange`,
    "description": currentBrand.description,
    "serviceType": "Gift Card Exchange",
    "provider": {
      "@type": "Organization",
      "name": "CashifyGCMart",
      "url": "https://cashifygcmart.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "url": canonicalUrl
  };

  const getPopularityStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star 
        key={index} 
        size={16} 
        className={index < rating ? 'text-yellow-500 fill-current' : 'text-gray-300'} 
      />
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="CashifyGCMart" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>
      {/* Brand Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(brandSchema)) }}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="text-6xl mb-4">{currentBrand.logo}</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Sell {currentBrand.name} Gift Cards for Cash
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            {currentBrand.description}
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-white/20 px-4 py-2 rounded-full">💰 {currentBrand.rate} Rate</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">⚡ Same Day Payment</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">🔒 100% Secure</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Current Rate & Stats */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">{currentBrand.rate}</div>
              <div className="text-sm text-gray-600">Current Rate</div>
            </div>
            <div>
              <div className="flex justify-center mb-2">{getPopularityStars(currentBrand.popularity)}</div>
              <div className="text-sm text-gray-600">Popularity</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">{currentBrand.marketShare}</div>
              <div className="text-sm text-gray-600">Market Share</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">{currentBrand.processingTime}</div>
              <div className="text-sm text-gray-600">Processing Time</div>
            </div>
          </div>
        </div>

        {/* Why Choose This Brand */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-8 text-gray-800">
            Why Sell {currentBrand.name} Gift Cards?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-green-600 flex items-center">
                <TrendingUp className="mr-2" size={24} />
                Key Benefits
              </h3>
              <ul className="space-y-3">
                {currentBrand.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="bg-green-100 text-green-600 rounded-full p-1 mt-1 flex-shrink-0">
                      ✓
                    </span>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4 text-blue-600 flex items-center">
                <Shield className="mr-2" size={24} />
                Expert Tips
              </h3>
              <ul className="space-y-3">
                {currentBrand.tips.map((tip, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="bg-blue-100 text-blue-600 rounded-full p-1 mt-1 flex-shrink-0">
                      💡
                    </span>
                    <span className="text-gray-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Rate Comparison */}
        <div className="bg-gray-50 rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">
            {currentBrand.name} vs Other Platforms
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white">
                  <th className="border border-gray-300 p-3 text-left">Platform</th>
                  <th className="border border-gray-300 p-3 text-left">{currentBrand.name} Rate</th>
                  <th className="border border-gray-300 p-3 text-left">Processing Time</th>
                  <th className="border border-gray-300 p-3 text-left">Fees</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-green-50">
                  <td className="border border-gray-300 p-3 font-semibold">Cashifygcmart</td>
                  <td className="border border-gray-300 p-3 text-green-600 font-bold">{currentBrand.rate}</td>
                  <td className="border border-gray-300 p-3">{currentBrand.processingTime}</td>
                  <td className="border border-gray-300 p-3 text-green-600">$0</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3">CardCash</td>
                  <td className="border border-gray-300 p-3">{parseInt(currentBrand.rate.split('-')[0]) - 5}-{parseInt(currentBrand.rate.split('-')[1].replace('%', '')) - 5}%</td>
                  <td className="border border-gray-300 p-3">1-2 days</td>
                  <td className="border border-gray-300 p-3">$0</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3">Gift Card Granny</td>
                  <td className="border border-gray-300 p-3">{parseInt(currentBrand.rate.split('-')[0]) - 8}-{parseInt(currentBrand.rate.split('-')[1].replace('%', '')) - 8}%</td>
                  <td className="border border-gray-300 p-3">2-5 days</td>
                  <td className="border border-gray-300 p-3">Varies</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* How to Sell Process */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-8 text-gray-800">
            How to Sell Your {currentBrand.name} Gift Card
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 text-blue-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
              <h3 className="font-semibold mb-2">Get Quote</h3>
              <p className="text-sm text-gray-600">Enter your {currentBrand.name} card details for instant rate</p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 text-green-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
              <h3 className="font-semibold mb-2">Submit Card</h3>
              <p className="text-sm text-gray-600">Upload images or enter card details manually</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 text-purple-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
              <h3 className="font-semibold mb-2">Verification</h3>
              <p className="text-sm text-gray-600">We verify your {currentBrand.name} card authenticity</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 text-orange-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">4</div>
              <h3 className="font-semibold mb-2">Get Paid</h3>
              <p className="text-sm text-gray-600">Receive cash within {currentBrand.processingTime}</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Sell Your {currentBrand.name} Gift Card?
          </h2>
          <p className="text-xl mb-6">
            Get {currentBrand.rate} of face value with same-day payment
          </p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <Link to="/rate-calculator" className="block sm:inline-block bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Calculate {currentBrand.name} Value
            </Link>
            <Link to="/form-submission" className="block sm:inline-block bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-colors border border-white">
              Sell {currentBrand.name} Card Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandPage;